import { useCallback, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import {
  faArrowRotateRight,
  faCirclePlay,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TeleophthPatientConfirmationModal from 'src/products/teleophth/pages/TeleophthPatientConfirmationModal/TeleophthPatientConfirmationModal';
import {
  APP_EVENTS,
  captureAppEvent,
  ORDER_SELECTION_SOURCE,
  reportCameraConnectionIssue,
  TABLE_CONTEXT,
} from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomTable from 'src/components/custom_components/CustomTable/CustomTable';
import WarnNoConnectedCameraModal from 'src/components/custom_components/WarnNoConnectedCameraModal';
import OrderDetailsButton from 'src/components/OrderDetailsButton';
import OrderDetailsDrawer from 'src/components/OrderDetailsDrawer';

import { DEFAULT_PAGE_SIZE } from 'src/models/DefaultPageSize.model';
import { I18NextNS } from 'src/models/Language.model';
import { Session } from 'src/models/Session.model';

import useCheckCameraConnection from 'src/hooks/useCheckCameraConnection';
import useDateFormats from 'src/hooks/useDateFormats';
import { OrderWithExam } from 'src/hooks/useGetFilterOrder';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

export type OpenOrdersTableProps = {
  ordersWithExams?: OrderWithExam[];
  totalPages?: number;
  isLoading: boolean;
  onFetchOrders: (pageNumber: number, pageSize: number) => void;
  onPageChange: (page: number) => void;
  currentPage?: number;
};

export default function OpenOrdersTable({
  ordersWithExams,
  totalPages,
  isLoading,
  onFetchOrders,
  onPageChange,
  currentPage = 1,
}: OpenOrdersTableProps) {
  const { t } = useTranslation(I18NextNS.COMMON);
  const { formatDate } = useDateFormats();

  const { canStartExam, isCameraConnectionLost } = useCheckCameraConnection();
  const { showHttpRequestError } = useHandleHttpRequestError();
  const { updateSession } = useRemoteBoundStore();

  const [confirmFormValues, setConfirmFormValues] = useState({});
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const [isOrderDetailsDrawerOpen, setIsOrderDetailsDrawerOpen] =
    useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderWithExam['order']>();
  const [
    shouldShowWarnNoConnectedCameraModal,
    setShouldShowWarnNoConnectedCameraModal,
  ] = useState(false);

  const handleStartOrResumeOrder = useCallback(
    async ({ order }: OrderWithExam) => {
      try {
        const tempSession: Partial<Session> = {
          patientUserId: order.owner?.userId,
          id: order.owner?.patientId,
          firstName: order.owner?.firstName,
          lastName: order.owner?.lastName,
          dob: order.owner?.dateOfBirth,
          orderId: order.data.OrderId.value,
          siteId: order.data.SiteId?.value,
          siteName: order.data.SiteName?.value,
          template: order.data.OrderType?.value,
        };
        setConfirmFormValues(tempSession);
        updateSession(tempSession);
        setIsShowConfirmModal(true);
        captureAppEvent(APP_EVENTS.ORDER_SELECTED, {
          selection_source: ORDER_SELECTION_SOURCE.OPEN_ORDERS,
        });
      } catch (error) {
        showHttpRequestError(error);
      }
    },
    [showHttpRequestError, updateSession],
  );

  return (
    <>
      <CustomTable
        isLoading={isLoading}
        dataSource={ordersWithExams ?? []}
        rowKey="id"
        tableContext={TABLE_CONTEXT.OPEN_ORDERS}
        pagination={{
          defaultPageSize: DEFAULT_PAGE_SIZE,
          pageSizeOptions: [DEFAULT_PAGE_SIZE],
          totalPages: totalPages ?? 0,
          currentPage: currentPage,
          handleSetCurrentPage: (page) => {
            onPageChange(page);
          },
          onChangePage: (page, pageSize) => {
            onFetchOrders(page - 1, pageSize);
          },
        }}
        rowProps={(record) =>
          ({
            'data-test': `order-row-${record.order?.owner?.patientId ?? record.id}`,
          }) as React.HTMLAttributes<HTMLTableRowElement>
        }
        columns={[
          {
            title: t('patientFields.DOB'),
            dataIndex: t('patientFields.DOB'),
            dataTest: 'order-cell-dob',
            headerDataTest: 'order-column-dob',
            render: ({ order }: OrderWithExam) =>
              formatDate(order.owner?.dateOfBirth),
          },
          {
            title: t('patientFields.First Name'),
            dataIndex: t('patientFields.First Name'),
            dataTest: 'order-cell-first-name',
            headerDataTest: 'order-column-first-name',
            render: ({ order }: OrderWithExam) => order.owner?.firstName,
          },
          {
            title: t('patientFields.Last Name'),
            dataIndex: t('patientFields.Last Name'),
            dataTest: 'order-cell-last-name',
            headerDataTest: 'order-column-last-name',
            render: ({ order }: OrderWithExam) => order.owner?.lastName,
          },
          {
            title: t('patientFields.PID'),
            dataIndex: t('patientFields.PID'),
            dataTest: 'order-cell-patient-id',
            headerDataTest: 'order-column-patient-id',
            render: ({ order }: OrderWithExam) => order.owner?.patientId,
          },
          {
            title: 'Sites',
            dataIndex: 'Sites',
            dataTest: 'order-cell-site',
            headerDataTest: 'order-column-site',
            render: ({ order }: OrderWithExam) => order.data?.SiteName?.value,
          },
          {
            title: t('orderFields.actions'),
            dataIndex: t('orderFields.actions'),
            headerDataTest: 'order-column-actions',
            render: (item: OrderWithExam) => {
              const icon = item.isResumable ? faArrowRotateRight : faCirclePlay;
              const iconTestId = item.isResumable
                ? 'resume-order-icon'
                : 'start-order-icon';
              return (
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-primary"
                    className="rounded-3 px-3 py-1"
                    onClick={() => {
                      if (!canStartExam) {
                        reportCameraConnectionIssue(isCameraConnectionLost);
                        setShouldShowWarnNoConnectedCameraModal(true);
                        return;
                      }
                      handleStartOrResumeOrder(item);
                    }}
                    data-test="start-or-resume-order-action"
                    data-track="exam.open-orders-standalone.resume-order"
                    data-observe
                  >
                    <FontAwesomeIcon icon={icon} data-test={iconTestId} />
                  </Button>
                  <OrderDetailsButton
                    onClick={() => {
                      setSelectedOrder(item.order);
                      setIsOrderDetailsDrawerOpen(true);
                    }}
                  />
                </div>
              );
            },
          },
        ]}
        empty={{ buttons: [], text: t('message.error.noOrdersToDisplay') }}
      />

      <TeleophthPatientConfirmationModal
        formValues={confirmFormValues}
        isShowConfirmModal={isShowConfirmModal}
        handleCloseConfirmModal={() => setIsShowConfirmModal(false)}
      />
      <WarnNoConnectedCameraModal
        isVisible={shouldShowWarnNoConnectedCameraModal}
        onClose={() => setShouldShowWarnNoConnectedCameraModal(false)}
      />

      <OrderDetailsDrawer
        show={isOrderDetailsDrawerOpen}
        onHide={() => setIsOrderDetailsDrawerOpen(false)}
        order={selectedOrder}
        onOrderCancelled={() => {
          onPageChange(1);
          onFetchOrders(0, DEFAULT_PAGE_SIZE);
        }}
      />
    </>
  );
}
